/**
 * Plugin de Sesión Simple y Elegante
 * Responsabilidad: Verificar token cada 10s y refrescar sesión automáticamente
 */
import { NON_REDIRECTABLE_AUTH_ERROR_PATHS } from '@/axiosConfig'
import { tokenService } from '@/services/TokenService'
import { playSoundById } from '@/utils/player'
import axios from '@/axiosConfig'

// Configuración simple
const HEARTBEAT_API_PATH = 'dashboard/core/access/heartbeat'
const TOKEN_CHECK_INTERVAL = 10000 // 10 segundos
const INACTIVITY_TIMEOUT = 1000 * 30 // 30 segundos
const REFRESH_INTERVAL_DIVISOR = 6 // Token time / 6
const DEFAULT_EVENTS = ['mousemove', 'mousedown', 'keypress', 'click', 'scroll']
const LOGIN_PATH = '/view/login'

/**
 * Calculadora de intervalos
 */
class IntervalCalculator {
  static getRefreshInterval() {
    const payload = tokenService.decodeTokenPayload()
    const fallbackMs = 60000 // 1 minuto por defecto

    return payload && payload.inactivityTimeout
      ? (payload.inactivityTimeout * 60 * 1000) / REFRESH_INTERVAL_DIVISOR
      : fallbackMs
  }
}

/**
 * Detector de actividad simple
 */
class ActivityWatcher {
  constructor() {
    this.isActive = false
    this.wasInactive = false
    this.lastActivity = Date.now()
    this.onActivityAfterInactivity = null
    this.boundHandler = () => this.markActivity()
  }

  start() {
    DEFAULT_EVENTS.forEach((event) => {
      window.addEventListener(event, this.boundHandler, { passive: true })
    })
  }

  stop() {
    DEFAULT_EVENTS.forEach((event) => {
      window.removeEventListener(event, this.boundHandler)
    })
  }

  markActivity() {
    const wasInactiveNow = this.isUserInactive()

    this.isActive = true
    this.lastActivity = Date.now()

    // Si estaba inactivo y ahora hay actividad, notificar
    if (wasInactiveNow && this.onActivityAfterInactivity) {
      this.wasInactive = false
      this.onActivityAfterInactivity()
    }
  }

  isUserInactive() {
    const inactive = Date.now() - this.lastActivity > INACTIVITY_TIMEOUT
    if (inactive && !this.wasInactive) {
      this.wasInactive = true
    }
    return inactive
  }

  setActivityCallback(callback) {
    this.onActivityAfterInactivity = callback
  }
}

/**
 * Monitor de token simple
 */
class TokenMonitor {
  constructor(onAlmostExpired) {
    this.onAlmostExpired = onAlmostExpired
    this.modalShown = false
  }

  check() {
    const secondsLeft = tokenService.getSecondsUntilExpiration()
    console.log(`🔍 Token check - Quedan ${secondsLeft}s`)

    this.handleAlertLogic(secondsLeft)
    return secondsLeft > 0
  }

  handleAlertLogic(secondsLeft) {
    const shouldShow =
      secondsLeft <= 65 && secondsLeft >= 55 && !this.modalShown
    const shouldHide = secondsLeft > 90

    if (shouldShow) {
      this.showAlert(secondsLeft)
      return
    }

    if (shouldHide) {
      this.hideAlert()
    }
  }

  showAlert(secondsLeft) {
    if (secondsLeft > 70) {
      return this.hideAlert()
    }

    this.modalShown = true
    playSoundById('01')

    console.log(
      `⚠️ Mostrando alerta de expiración - Quedan ${secondsLeft} segundos`
    )

    const event = new CustomEvent('token-almost-expired', {
      detail: { secondsLeft, warningThreshold: 60 },
    })
    window.dispatchEvent(event)
  }

  hideAlert() {
    this.modalShown = false
  }

  reset() {
    this.modalShown = false
  }
}

/**
 * Detector de cambios de ruta más confiable
 */
class RouteWatcher {
  constructor(onRouteChange) {
    this.onRouteChange = onRouteChange
    this.currentPath = location.pathname
    this.observer = null
    this.checkTimer = null
    this.isRunning = false
  }

  start() {
    if (this.isRunning) return
    
    console.log('🔄 Iniciando detector de rutas')
    this.isRunning = true
    
    // Método 1: Interceptar métodos del history
    this.interceptHistoryMethods()
    
    // Método 2: Polling cada 100ms como respaldo
    this.startPolling()
    
    // Método 3: Escuchar eventos del navegador
    window.addEventListener('popstate', this.handleRouteCheck)
    window.addEventListener('hashchange', this.handleRouteCheck)
  }

  stop() {
    if (!this.isRunning) return
    
    console.log('🔄 Deteniendo detector de rutas')
    this.isRunning = false
    
    // Limpiar polling
    if (this.checkTimer) {
      clearInterval(this.checkTimer)
      this.checkTimer = null
    }

    // Restaurar métodos originales
    this.restoreHistoryMethods()
    
    // Remover event listeners
    window.removeEventListener('popstate', this.handleRouteCheck)
    window.removeEventListener('hashchange', this.handleRouteCheck)
  }

  startPolling() {
    // Polling cada 100ms como respaldo
    this.checkTimer = setInterval(() => {
      this.checkRouteChange()
    }, 1000)
  }

  interceptHistoryMethods() {
    // Guardar referencias originales
    this.originalPushState = history.pushState
    this.originalReplaceState = history.replaceState

    // Interceptar pushState
    history.pushState = (...args) => {
      this.originalPushState.apply(history, args)
      setTimeout(() => this.checkRouteChange(), 0)
    }

    // Interceptar replaceState
    history.replaceState = (...args) => {
      this.originalReplaceState.apply(history, args)
      setTimeout(() => this.checkRouteChange(), 0)
    }
  }

  restoreHistoryMethods() {
    if (this.originalPushState) {
      history.pushState = this.originalPushState
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState
    }
  }

  handleRouteCheck = () => {
    this.checkRouteChange()
  }

  checkRouteChange() {
    const newPath = location.pathname
    
    if (this.currentPath !== newPath) {
      console.log(`🔄 Cambio de ruta detectado: ${this.currentPath} → ${newPath}`)
      this.currentPath = newPath
      this.onRouteChange(newPath)
    }
  }
}

/**
 * Coordinador de sesión simple y elegante
 */
class SimpleSessionCoordinator {
  constructor() {
    this.activityWatcher = new ActivityWatcher()
    this.tokenMonitor = new TokenMonitor()
    this.routeWatcher = new RouteWatcher((path) => this.handleRouteChange(path))
    this.tokenCheckTimer = null
    this.heartbeatTimer = null
    this.isRunning = false
    this.heartbeatPaused = false
    this.currentPath = location.pathname
    
    this.init()
  }

  init() {
    // Ejecutar cuando el documento esté listo
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      console.log('📄 Documento ya cargado, iniciando sistema')
      this.launch()
    } else {
      console.log('⏳ Esperando carga del documento...')
      document.addEventListener('DOMContentLoaded', () => {
        console.log('📄 Documento cargado, iniciando sistema')
        this.launch()
      })
    }
  }

  handleRouteChange(path) {
    console.log(`🔄 Procesando cambio de ruta: ${path}`)
    this.currentPath = path
    this.launch()
  }

  launch() {
    // SIEMPRE iniciar el detector de rutas (incluso en login)
    if (!this.routeWatcher.isRunning) {
      this.routeWatcher.start()
    }
    
    if (NON_REDIRECTABLE_AUTH_ERROR_PATHS.includes(this.currentPath)) {
      console.log('🚫 En página de login - deteniendo SOLO sistema de sesión (detector de rutas sigue activo)')
      this.stopSessionSystem()
      return
    }

    if (!this.isRunning) {
      console.log('🔄 Iniciando coordinador de sesión')
      this.start()
    }
  }

  start() {
    if (this.isRunning) return

    console.log('✅ Iniciando coordinador simple de sesión')
    this.isRunning = true

    // Configurar callback para reactivación después de inactividad
    this.activityWatcher.setActivityCallback(() =>
      this.handleActivityAfterInactivity()
    )

    this.activityWatcher.start()
    this.startTokenChecking()
    this.startHeartbeat()
  }

  startTokenChecking() {
    this.tokenCheckTimer = setInterval(() => {
      const isValid = this.tokenMonitor.check()
      if (!isValid) {
        this.handleExpiredToken()
      }
    }, TOKEN_CHECK_INTERVAL)
  }

  startHeartbeat() {
    const interval = IntervalCalculator.getRefreshInterval()
    console.log(`🔍 Intervalo de heartbeat: ${interval}ms`)

    this.heartbeatTimer = setInterval(() => {
      this.attemptHeartbeat()
    }, interval)
  }

  attemptHeartbeat() {
    if (this.activityWatcher.isUserInactive()) {
      if (!this.heartbeatPaused) {
        console.log('Usuario inactivo, pausando heartbeat')
        this.heartbeatPaused = true
      }
      return
    }

    this.performHeartbeat()
  }

  async performHeartbeat() {
    try {
      if(this.tokenMonitor.modalShown) {
        return
      }

      await axios.get(HEARTBEAT_API_PATH)
      console.log('✓ Heartbeat exitoso')
    } catch (error) {
      console.error('❌ Error en heartbeat:', error.message)
    }
  }

  handleActivityAfterInactivity() {
    if(this.tokenMonitor.modalShown) {
      return
    }

    console.log('🔄 Actividad detectada después de inactividad, reactivando heartbeat')
    this.heartbeatPaused = false

    this.performHeartbeat()
  }

  handleExpiredToken() {
    console.log('🚨 Token expirado, redirigiendo...')
    this.stop()
    window.location.href = LOGIN_PATH
  }

  handleContinueModal() {
    console.log('🔄 Usuario hizo click en "Estoy aquí"')
    this.tokenMonitor.reset()
    this.activityWatcher.markActivity()
    this.performHeartbeat()
  }

  restart() {
    console.log('🔄 Reiniciando coordinador de sesión')
    this.stop()
    this.start()
  }

  stopSessionSystem() {
    console.log('🔄 Deteniendo SOLO sistema de sesión (detector de rutas sigue activo)')
    this.isRunning = false
    this.heartbeatPaused = false
    
    this.activityWatcher.stop()
    // NO detener routeWatcher aquí

    if (this.tokenCheckTimer) {
      clearInterval(this.tokenCheckTimer)
      this.tokenCheckTimer = null
    }

    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  stop() {
    console.log('🔄 Deteniendo coordinador de sesión COMPLETO')
    this.isRunning = false
    this.heartbeatPaused = false
    
    this.activityWatcher.stop()
    this.routeWatcher.stop()

    if (this.tokenCheckTimer) {
      clearInterval(this.tokenCheckTimer)
      this.tokenCheckTimer = null
    }

    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }
}

// Instancia global
let coordinator = null

export default {
  install(Vue, options = {}) {
    coordinator = new SimpleSessionCoordinator()

    // Métodos globales simples
    Vue.prototype.$refreshSession = () => coordinator.performHeartbeat()
    Vue.prototype.$resetSessionRefresh = () => coordinator.restart()
    Vue.prototype.$handleContinueModal = () => coordinator.handleContinueModal()
  },
}
